import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from './authService';

//Get user from localStorage
const user = JSON.parse(sessionStorage.getItem('user'));

// reducer initial state
const initialState = {
	user: user ? user : null,
	users: [],
	isError: false,
	isSuccess: false,
	isLoading: false,
	message: '',
	success: '',
	profile: null,
	lastLogin: null
};

// Get all users
export const getAllUsers = createAsyncThunk('auth/getAll', async (_, thunkAPI) => {
	try {
		const response = await authService.allUsers();
		return response.data || response;
	} catch (error) {
		const message =
			(error.response && error.response.data && error.response.data.error?.message) || 
			error.response?.data?.message || 
			error.message || 
			error.toString();
		return thunkAPI.rejectWithValue(message);
	}
});

// Register user (Admin only)
export const register = createAsyncThunk('auth/register', async (userData, thunkAPI) => {
	try {
		console.log(`slice`,userData);
		const response = await authService.register(userData);
		return response.data || response;
	} catch (error) {
		const message =
			(error.response && error.response.data && error.response.data.error?.message) || 
			error.response?.data?.message || 
			error.message || 
			error.toString();
		return thunkAPI.rejectWithValue(message);
	}
});



// Get user profile
export const getProfile = createAsyncThunk('auth/getProfile', async (userId, thunkAPI) => {
	try {
		const response = await authService.getProfile(userId);
		return response.data || response;
	} catch (error) {
		const message =
			(error.response && error.response.data && error.response.data.error?.message) || 
			error.response?.data?.message || 
			error.message || 
			error.toString();
		return thunkAPI.rejectWithValue(message);
	}
});

// Update user profile
export const updateProfile = createAsyncThunk('auth/updateProfile', async ({ userId, userData }, thunkAPI) => {
	try {
		const response = await authService.updateProfile(userId, userData);
		return response.data || response;
	} catch (error) {
		const message =
			(error.response && error.response.data && error.response.data.error?.message) || 
			error.response?.data?.message || 
			error.message || 
			error.toString();
		return thunkAPI.rejectWithValue(message);
	}
});

// Delete user (Admin only)
export const deleteUser = createAsyncThunk('auth/deleteUser', async (userId, thunkAPI) => {
	try {
		const response = await authService.deleteUser(userId);
		return { userId, response: response.data || response };
	} catch (error) {
		const message =
			(error.response && error.response.data && error.response.data.error?.message) || 
			error.response?.data?.message || 
			error.message || 
			error.toString();
		return thunkAPI.rejectWithValue(message);
	}
});

// Login user
export const login = createAsyncThunk('auth/login', async (userData, thunkAPI) => {
	try {
		const response = await authService.login(userData);
		return response.data || response;
	} catch (error) {
		const message =
			(error.response && error.response.data && error.response.data.error?.message) || 
			error.response?.data?.message || 
			error.message || 
			error.toString();
		return thunkAPI.rejectWithValue(message);
	}
});

// Logout
export const logout = createAsyncThunk('auth/logout', async () => {
	authService.logout();
});

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		// reset state to the initial state
		reset: (state) => {
			state.isLoading = false;
			state.isSuccess = false;
			state.isError = false;
			state.message = '';
			state.success = '';
		},
		// Update user profile in state
		updateUserInState: (state, action) => {
			state.user = { ...state.user, ...action.payload };
		},
		// Clear auth state
		clearAuth: (state) => {
			state.user = null;
			state.profile = null;
			state.lastLogin = null;
			state.isError = false;
			state.isSuccess = false;
			state.message = '';
			state.success = '';
		}
	},
	extraReducers: (builder) => {
		builder
			// Get all users
			.addCase(getAllUsers.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(getAllUsers.fulfilled, (state, action) => {
				state.isLoading = false;
				state.isSuccess = true;
				state.users = action.payload;
			})
			.addCase(getAllUsers.rejected, (state, action) => {
				state.isLoading = false;
				state.isError = true;
				state.isSuccess = false;
				state.message = action.payload;
			})
			// Register user
			.addCase(register.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(register.fulfilled, (state, action) => {
				state.isLoading = false;
				state.isSuccess = true;
				state.success = action.payload.message || 'User registered successfully';
			})
			.addCase(register.rejected, (state, action) => {
				state.isLoading = false;
				state.isError = true;
				state.isSuccess = false;
				state.message = action.payload;
			})
			// Get profile
			.addCase(getProfile.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(getProfile.fulfilled, (state, action) => {
				state.isLoading = false;
				state.isSuccess = true;
				state.profile = action.payload;
			})
			.addCase(getProfile.rejected, (state, action) => {
				state.isLoading = false;
				state.isError = true;
				state.isSuccess = false;
				state.message = action.payload;
			})
			// Update profile
			.addCase(updateProfile.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(updateProfile.fulfilled, (state, action) => {
				state.isLoading = false;
				state.isSuccess = true;
				state.profile = action.payload;
				state.user = { ...state.user, ...action.payload };
				state.success = action.payload.message || 'Profile updated successfully';
			})
			.addCase(updateProfile.rejected, (state, action) => {
				state.isLoading = false;
				state.isError = true;
				state.isSuccess = false;
				state.message = action.payload;
			})
			// Delete user
			.addCase(deleteUser.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(deleteUser.fulfilled, (state, action) => {
				state.isLoading = false;
				state.isSuccess = true;
				state.users = state.users.filter(user => user._id !== action.payload.userId);
				state.success = 'User deleted successfully';
			})
			.addCase(deleteUser.rejected, (state, action) => {
				state.isLoading = false;
				state.isError = true;
				state.isSuccess = false;
				state.message = action.payload;
			})
			// Login user
			.addCase(login.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(login.fulfilled, (state, action) => {
				state.isLoading = false;
				state.isSuccess = true;
				state.user = action.payload;
				state.lastLogin = new Date().toISOString();
				state.success = action.payload.message || 'Login successful';
			})
			.addCase(login.rejected, (state, action) => {
				state.isLoading = false;
				state.isError = true;
				state.isSuccess = false;
				state.message = action.payload;
				state.user = null;
			})
			// Logout
			.addCase(logout.fulfilled, (state) => {
				state.user = null;
				state.profile = null;
				state.lastLogin = null;
				state.isSuccess = false;
				state.isError = false;
				state.message = '';
				state.success = '';
			});
	}
});

export const { reset, updateUserInState, clearAuth } = authSlice.actions;
export default authSlice.reducer;
